/**
 * Contrat d'API — Suivi (SLX-078, Sprint 12), console tuteur/parent.
 *
 * Agrégats en LECTURE SEULE consommés par la fiche élève du dashboard :
 * progression par savoir, dose de travail, évaluations diagnostiques,
 * recommandations d'intervention tuteur. Simples comptages/moyennes sur les
 * tables existantes (`MaitriseSavoir`, `TentativeExercice`,
 * `EvaluationPreliminaire`) — PAS un moteur d'analytics. Implémentation côté
 * `@silex/api` (`modules/suivi/`).
 *
 * AUCUN niveau scolaire officiel n'est jamais exposé, AUCUN classement entre
 * élèves (cf. `docs/scrum/SPRINT-12.md`, garde-fous produit).
 *
 * ─── La double dimension (SPEC-REPRESENTATION-VISUELLE §5, F7/F8) ──────────
 *
 * Un savoir VISUEL (géométrie, fonctions, statistiques) se mesure sur DEUX
 * dimensions distinctes : « sait calculer » et « sait lire la figure ». Elles
 * sont mesurées, affichées, remédiées et validées séparément. Il n'existe
 * NULLE PART une valeur qui les résume : ni moyenne, ni pourcentage, ni
 * minimum ordinal, ni « niveau global ». `ProgressionSavoirDto` porte donc
 * `dimension` et le contrat renvoie UNE LIGNE PAR (savoir, dimension) — deux
 * lignes pour un savoir visuel, une seule pour un savoir d'algèbre.
 *
 * Ce contrat est le reflet exact de la clé `@@unique([eleveId, savoirId,
 * dimension])` de `MaitriseSavoir` : deux LIGNES, jamais deux colonnes, parce
 * que deux lignes ne peuvent pas être moyennées par accident.
 *
 * NOTE orchestrateur : nouveau fichier — à ajouter au barrel
 * `packages/shared/src/types/index.ts` (ligne ajoutée en fin de fichier par ce
 * lot ; à recomposer en cas de conflit avec un autre lot du même sprint).
 */
/**
 * Niveau de maîtrise d'un savoir pour un élève — miroir de l'enum Prisma
 * `NiveauMaitrise` (module `pda`). Écrit par le futur moteur adaptatif
 * (modules `adaptive`/`assessment`, pas encore livrés à ce sprint) ; ce
 * contrat existe déjà pour que le dashboard affiche les données dès qu'elles
 * apparaissent, sans nouvelle migration de contrat.
 */
export const NIVEAUX_MAITRISE = [
    'non_evalue',
    'en_decouverte',
    'en_consolidation',
    'en_voie_de_maitrise',
    'maitrise',
];
/* ───────────────────────────── la double dimension ───────────────────────── */
/**
 * Les deux dimensions d'un savoir — miroir de l'enum Prisma `DimensionSavoir`.
 *
 * `calcul` est la dimension par défaut et la SEULE d'un savoir non visuel :
 * toute mesure existante avant ce lot est, par construction, une mesure de
 * calcul (`@default(calcul)` côté base). `lecture_figure` n'existe que pour
 * les savoirs dont l'objet d'étude EST une représentation.
 *
 * Elles ne sont JAMAIS additionnées, moyennées, ni réduites à une seule
 * valeur — pas même par un minimum ordinal (« le savoir prend le palier le
 * plus bas des deux »), qui serait arithmétiquement innocent et
 * pédagogiquement faux : il masquerait la force en calcul d'un élève qui lit
 * mal, et cacherait la seule chose à travailler.
 */
export const DIMENSIONS_SAVOIR = ['calcul', 'lecture_figure'];
/**
 * OÙ une maîtrise a été observée — miroir de l'enum Prisma `ContexteMaitrise`
 * (Sprint 32).
 *
 * L'annexe du pilote l'a démontré sur une élève réelle : une règle tenue en
 * exercice ciblé s'évapore en rédaction. Ce n'est pas un oubli, c'est le
 * transfert qui n'est pas fait — et il est INDÉTECTABLE sur une fiche isolée,
 * parce qu'il ne se voit qu'en comparant deux contextes.
 *
 * Même discipline que `DimensionSavoir` : les deux contextes sont deux LIGNES
 * de `MaitriseSavoir`, jamais deux colonnes de la même ligne, et jamais une
 * moyenne. Un élève qui accorde juste en exercice et faux en rédaction n'est
 * pas « à moitié bon » : il sait la règle et ne la transfère pas encore, ce
 * qui appelle un rappel espacé EN PRODUCTION, pas un exercice de plus.
 */
export const CONTEXTES_MAITRISE = ['exercice_isole', 'production_libre'];
/** Ce que l'élève lit. Jamais « exercice isolé » ni « production libre ». */
export const LIBELLES_CONTEXTE = {
    exercice_isole: 'En exercice',
    production_libre: 'Quand tu écris',
};
/**
 * Libellés normatifs, tels qu'écrits dans la maquette v5 (`geometrie.html`,
 * écrans G4/G7/G8, classe `.dims-lbl`). Élève et parent voient ces mots-là ;
 * aucun écran n'en invente d'autres.
 */
export const LIBELLES_DIMENSION = {
    calcul: 'Calculer',
    lecture_figure: 'Lire la figure',
};
/**
 * Libellé de la colonne unique d'un savoir qui ne porte pas de figure (G8,
 * carte « Algèbre — secondaire 3 »). La deuxième colonne n'apparaît que là où
 * elle a un sens, et l'écran le dit.
 */
export const LIBELLE_DIMENSION_UNIQUE = 'Une seule lecture';
/**
 * Les trois catégories PDA où l'objet d'étude EST une représentation. Règle de
 * comptage de la maquette, reprise telle quelle et rendue exécutable.
 *
 * Valeurs littérales de `SavoirPDA.categorie` dans le référentiel chargé
 * (`packages/api/prisma/seed.ts`).
 */
export const CATEGORIES_PDA_VISUELLES = [
    'Géométrie',
    'Fonctions',
    'Statistiques et probabilités',
];
/**
 * Un savoir porte-t-il la dimension « lire la figure » ?
 *
 * La qualification est DÉRIVÉE de `SavoirPDA.categorie` — colonne déjà en base
 * — et jamais d'une nouvelle colonne ni d'une liste de codes tenue à la main,
 * qui divergerait du référentiel au premier savoir ajouté.
 *
 * Fonction unique, consommée par l'API (au point d'ÉCRITURE autant qu'à
 * l'affichage : un savoir non visuel n'a jamais de ligne `lecture_figure`),
 * par l'application et par les tests. Prédicat pur, sans dépendance : c'est la
 * seule « logique » de ce paquet de contrat, et elle y vit parce qu'un
 * désaccord entre le client et le serveur sur ce prédicat ferait apparaître ou
 * disparaître une colonne à l'écran.
 */
export function savoirPorteLaLectureDeFigure(categorie) {
    if (categorie === null || categorie === undefined)
        return false;
    return CATEGORIES_PDA_VISUELLES.includes(categorie);
}
/**
 * Les dimensions effectivement portées par un savoir, dans l'ordre
 * d'affichage de la maquette (G8 : « Lire la figure » à gauche du filet,
 * « Calculer » à droite).
 */
export function dimensionsDuSavoir(categorie) {
    return savoirPorteLaLectureDeFigure(categorie) ? ['lecture_figure', 'calcul'] : ['calcul'];
}
/** `GET /v1/suivi/:eleveId/dose` — fenêtre glissante, `semaine` par défaut. */
export const PERIODES_DOSE = ['semaine', 'mois'];
/**
 * Seuil (jours) au-delà duquel un savoir « à surveiller » sans nouvelle
 * observation déclenche une recommandation d'intervention tuteur — voir
 * `RecommandationsEleveResponse.critere` pour la formulation complète du
 * critère, retournée telle quelle par l'API pour rester la source unique de
 * vérité (documentée une fois, affichée partout).
 */
export const SEUIL_JOURS_SANS_PROGRES_RECOMMANDATION = 14;
/* ─────────────────────── le journal d'observations (F11) ─────────────────── */
/**
 * D'où vient une observation. `MaitriseSavoir` est un AGRÉGAT ; ceci est le
 * JOURNAL — un fait daté par observation, jamais agrégé.
 *
 * L'en-tête de `aide-devoirs/maitrise.service.ts` réclame ce journal depuis sa
 * première ligne : sans lui, le vrai critère FR-5.6 (« ≥ 3 observations dont
 * une différée ≥ 7 jours ») ne peut être qu'approximé, et les écrans G4
 * (les trois dernières figures d'un élève) et G8 (trois faits datés) n'ont
 * rien à afficher.
 */
/*
 * LE REGISTRE DIT CE QUI S'ÉCRIT (chantier « manuel vivant », MV-SOC-03, AB#817).
 * Il annonçait quatre valeurs ; le code en écrivait trois autres, connues de
 * lecteurs épars (carte 02, § 5.1). Elles y sont maintenant, avec celles que la
 * voie d'exercice du manuel écrit — les mêmes noms que le journal par notion
 * (`SOURCES_OBSERVATION_NOTION`), pour qu'un même travail ne porte pas deux
 * noms selon le grain. `registres-manuel-vivant.test.ts` lit chaque écrivain et
 * refuse une valeur hors d'ici.
 */
export const SOURCES_OBSERVATION = [
    /** La boucle d'exercice du V1. */
    'exercice',
    'evaluation',
    'aide_devoirs',
    'revue_tuteur',
    /** La grille de l'évaluation préliminaire. */
    'evaluation_preliminaire',
    /** La correction d'un défi, d'une épreuve d'entrée. */
    'defi_du_jour',
    /** La correction d'un devoir photographié. */
    'copie_devoir',
    /** Un exercice du manuel : choisi, déposé par le tuteur, proposé par une capsule. */
    'exercice_manuel',
    'sequence_tuteur',
    'capsule',
];
//# sourceMappingURL=suivi.js.map